import { HoverCard } from '@mantine/core';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDialog } from '../../Hooks/useAppDialog';
import AppDialogMessage from '../Common/AppDialogMessage';
import { getStores } from '../../api/storeApi';
interface BolReportDownloadProps {
  user?: string;
}

const BolReportDownload: React.FC<BolReportDownloadProps> = ({ user }) => {
  const [, setLoading] = useState<boolean>(false);
  const token = useSelector((state: any) => state.user.sessionId);
  const [modal, setModal] = useState<boolean>(false);
  const { dialog, openDialog, closeDialog } = useAppDialog();
  // set the date state with start_dt and end_dt as current date in YYYY-MM-DD format and last 30 days
  const [date, setDate] = useState<{ start_dt: string; end_dt: string }>({
    start_dt: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
    end_dt: new Date().toISOString().split("T")[0],
  });
  const [filter, setFilter] = useState<{
    bol_type: 'ALL' | 'EXTERNAL' | 'INTERNAL';
    carrier: string;
    dclocation: string;
    payment_status: 'true' | 'false' | 'ALL';
    approval: 'true' | 'false' | 'ALL';
    dc_received: 'true' | 'false' | 'ALL';
    vendor_delivered: 'true' | 'false' | 'ALL';
    corporate_no: string;
  }>({
    bol_type: 'ALL',
    carrier: 'G2000',
    dclocation: '',
    payment_status: 'ALL',
    approval: 'ALL',
    dc_received: 'ALL',
    vendor_delivered: 'ALL',
    corporate_no: '',
  });
  const [corportateList, setCorporateList] = useState<any>([]);
  useEffect(() => {
    getStores().then((data) => {
      const uniqueCorporateNos = Array.from(
        new Set(data.map((st: any) => st.corporate_no))
      );
      setCorporateList(uniqueCorporateNos);
    }).catch((error) => { console.log(error) })
  }, []);

  const handleFilterChange = (key: string, value: string) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  // Function to download the BOL report PDF
  const downloadBolReport = async (date: {
    start_dt: string;
    end_dt: string;
  }) => {
    setLoading(true);
    let reportUrl = '/bol/getBolReport';
    if (user === 'SuperAdmin') {
      reportUrl = '/bol/getCarrierBolReport';
    }
    try {
      // use fetch to get the PDF from the backend
      const response = await fetch(reportUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...date, ...filter }),
      });
      if (response.status === 204) {
        return alert(`No data found`)
      }
      if (response.status === 500) {
        return alert(`An error occurred while fetching BOL report.`)
      }
      // Get the PDF Blob from the response
      const data = await response.blob();

      openDialog({
        variant: 'success',
        title: 'Success',
        message: 'BOL report downloaded successfully.',
        confirmText: 'OK',
        // onConfirm: () => console.log('User confirmed'),
      });

      // Create a Blob URL for the PDF data
      const pdfUrl = window.URL.createObjectURL(data);

      const a = document.createElement("a");
      a.href = pdfUrl;

      // Set the custom filename (e.g., BOL_Report_YYYY-MM-DD.pdf)
      const fileName = `BOL_Report_${date.start_dt}_to_${date.end_dt}.pdf`;
      a.download = fileName;

      // Append the anchor, trigger the download, and remove the anchor
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Open the PDF in a new tab
      window.open(pdfUrl, "_blank");

      // Optionally, clean up the object URL after opening the PDF
      window.URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <Dialog
        open={modal}
        onClose={() => setModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Download BOL Report</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  value={date.start_dt}
                  onChange={(e) =>
                    setDate((prev: any) => ({ ...prev, start_dt: e.target.value }))
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  value={date.end_dt}
                  onChange={(e) =>
                    setDate((prev: any) => ({ ...prev, end_dt: e.target.value }))
                  }
                />
              </Grid>

              {user !== 'SuperAdmin' && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Type"
                    value={filter.bol_type || 'ALL'}
                    onChange={(e) => handleFilterChange('bol_type', e.target.value || 'ALL')}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="EXTERNAL">EXTERNAL</MenuItem>
                    <MenuItem value="INTERNAL">INTERNAL</MenuItem>
                  </TextField>
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Carrier"
                  value={filter.carrier || ''}
                  onChange={(e) => handleFilterChange('carrier', e.target.value || 'G2000')}
                >
                  <MenuItem value="G2000">G2000</MenuItem>
                  <MenuItem value="SVP SPORTS">SVP SPORTS</MenuItem>
                  <MenuItem value="OTHER">OTHER</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Payment"
                  value={filter.payment_status || ''}
                  onChange={(e) => handleFilterChange('payment_status', e.target.value)}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="false">Pending</MenuItem>
                  <MenuItem value="true">Approved</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Corporation"
                  value={filter.corporate_no || ''}
                  onChange={(e) => handleFilterChange('corporate_no', e.target.value)}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  {corportateList.map((item: any) => (
                   <MenuItem
  key={typeof item === 'string' ? item : item?.value}
  value={typeof item === 'string' ? item : item?.value}
>
  {typeof item === 'string' ? item : item?.label}
</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Approval"
                  value={filter.approval || ''}
                  onChange={(e) => handleFilterChange('approval', e.target.value)}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="false">Pending</MenuItem>
                  <MenuItem value="true">Approved</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setModal(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => downloadBolReport(date)}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
      <HoverCard>
        <HoverCard.Target>
          <ReceiptLongTwoToneIcon sx={{ cursor: 'pointer', '&:hover': { color: 'secondary.main' } }} onClick={() => setModal(true)} />
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Stack sx={{ p: 2 }}>
            <span style={{ fontWeight: 'bold' }}>Download BOL Report</span>
            <span>Click to download the Bill of Lading report as a PDF.</span>
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
      <AppDialogMessage
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        hideCancel={dialog.hideCancel}
        onConfirm={dialog.onConfirm}
        onClose={closeDialog}
      />
    </div>
  );
};

export default BolReportDownload;